import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge, getStatusBadgeVariant, Pagination, PolicyNumber } from '@/components/common';
import { useQuotes } from '@/hooks/useQuotes';
import { useClaims } from '@/hooks/useClaims';
import { useRenewalQueue } from '@/hooks/useRenewals';
import { formatDate, formatCurrency, titleCase } from '@/utils';
import type { RenewalQueueRow } from '@/types/renewalQueue';
import { renewalWorkflowStatusLabel } from '@/pages/renewals/renewalStatusOptions';

/**
 * One contact's insurance, in four tabs — report item 9.
 *
 * EVERY TAB IS SCOPED TO THE CONTACT, and that is the whole safety property.
 * A list rendered here without a contact id is not empty or broken: it is the
 * broker's entire book, displayed under one person's name, with real rows that
 * look completely correct. The backend refuses a malformed contact id rather
 * than answering with everything (`contact_group_ids`); this passes the id on
 * every query so the question is never asked unfiltered.
 *
 * A contact row belongs to ONE broker. A person's LOGIN may own a contact row
 * at several brokers, but the id this component is given is a contact row, not
 * a login, so a broker's space stays sealed without this component knowing
 * anything about brokers.
 *
 * Policies is the default tab: it is what the page showed before, so the
 * Contact Details policy tests, and anyone's habits, land where they always
 * did. Its panel is PASSED IN rather than rebuilt here -- it already carries an
 * Active/Archive split, its own paging and a board #279 rule about archived
 * policies staying in a person's history. A second policies table beside it
 * would be two implementations of one question, and they would drift.
 *
 * Counts come from each list's own `count`, so all four load on mount. That is
 * three requests more than before on a page that already made several, and it
 * is the price of a tab strip that tells the truth about what is behind it —
 * a count that appears only after you click is not a count.
 */

type TabId = 'quotes' | 'policies' | 'claims' | 'renewals';

const PAGE_SIZE = 20;

export function ContactRecordTabs({
  customerId,
  policiesPanel,
  policyCount,
}: {
  customerId: string;
  /** The page's existing Policies card body, rendered under the Policies tab. */
  policiesPanel: ReactNode;
  /** Its count, which that panel already knows and this component must not
   *  re-fetch just to label a tab. */
  policyCount: number;
}) {
  const [activeTab, setActiveTab] = useState<TabId>('policies');
  // A page number PER TAB. One shared number meant paging Claims to 3 re-issued
  // the Quotes query as page 3 of a one-page list -- DRF raises NotFound for an
  // out-of-range page, so that query errored and the Quotes badge collapsed.
  const [pages, setPages] = useState<Record<TabId, number>>({
    quotes: 1,
    policies: 1,
    claims: 1,
    renewals: 1,
  });
  const page = pages[activeTab];
  const setPage = (next: number) => setPages((prev) => ({ ...prev, [activeTab]: next }));

  // `customer` here is a CONTACT id. The backend widens it to that contact and
  // its duplicate rows (`contact_group_ids`) -- the same grouping the policies
  // panel uses -- so the four tabs cannot disagree about who this person is.
  const quotes = useQuotes(pages.quotes, { customer: customerId });
  const claims = useClaims(pages.claims, { customer: customerId });
  // B9: reads the policy-based renewal queue, not the old Renewal workflow
  // table. `bucket: 'all', days: 'all'` asks for every policy in this
  // contact's renewal history regardless of window -- this tab is a record of
  // everything, not a worklist filtered to what's due soon.
  const renewals = useRenewalQueue(pages.renewals, { customer: customerId, bucket: 'all', days: 'all' });

  // A count is `null` until its list has answered. Rendering 0 while a request
  // is in flight (or after it failed) tells a broker the contact has no claims
  // when the truth is that nobody has looked yet -- and a glance at the strip is
  // exactly how this screen gets used.
  const countOf = (query: { data?: { count: number }; isSuccess: boolean }) =>
    query.isSuccess ? (query.data?.count ?? 0) : null;

  const tabs: { id: TabId; label: string; count: number | null }[] = [
    { id: 'quotes', label: 'Quotes', count: countOf(quotes) },
    { id: 'policies', label: 'Policies', count: policyCount },
    { id: 'claims', label: 'Claims', count: countOf(claims) },
    { id: 'renewals', label: 'Renewals', count: countOf(renewals) },
  ];

  const active = { quotes, claims, renewals }[activeTab as Exclude<TabId, 'policies'>];
  // Derived, not read off the payload: `PaginatedResponse` carries only
  // `count`, and reading a `total_pages` that is not in the type would have
  // been `undefined` at runtime -- one page, silently, however many there are.
  const totalPages = Math.max(1, Math.ceil((active?.data?.count ?? 0) / PAGE_SIZE));

  return (
    <Card padding="none">
      <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <nav className="flex space-x-6 px-6" role="tablist" aria-label="Contact records">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:hover:border-gray-600 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {tab.count === null ? '…' : tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      <div role="tabpanel">
        {activeTab === 'policies' ? (
          policiesPanel
        ) : active!.isLoading ? (
          <p className="px-6 py-8 text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        ) : active!.isError ? (
          // "No claims for this contact" on a FAILED request is a statement of
          // fact the page has no basis for. Not knowing is its own answer.
          <p className="px-6 py-8 text-sm text-amber-700 dark:text-amber-300">
            Could not load this contact&apos;s{' '}
            {tabs.find((t) => t.id === activeTab)!.label.toLowerCase()}. That is not the same as
            there being none — please try again.
          </p>
        ) : (active!.data?.results.length ?? 0) === 0 ? (
          <EmptyTab label={tabs.find((t) => t.id === activeTab)!.label} />
        ) : (
          <>
            {activeTab === 'quotes' && <QuotesTable rows={quotes.data!.results} />}
            {activeTab === 'claims' && <ClaimsTable rows={claims.data!.results} />}
            {activeTab === 'renewals' && <RenewalsTable rows={renewals.data!.results} />}
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={active!.data?.count ?? 0}
                itemsPerPage={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function EmptyTab({ label }: { label: string }) {
  return (
    <p className="px-6 py-8 text-sm text-gray-500 dark:text-gray-400">
      No {label.toLowerCase()} for this contact yet.
    </p>
  );
}

function RecordTable({ headers, children }: { headers: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {headers.map((header) => (
              <th
                key={header}
                className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">{children}</tbody>
      </table>
    </div>
  );
}

/** The record's own identifier, as the link to its page. */
function RecordLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="font-medium text-primary-600 hover:underline dark:text-primary-400">
      {children}
    </Link>
  );
}

function QuotesTable({ rows }: { rows: Record<string, any>[] }) {
  return (
    <RecordTable headers={['Quote', 'Type', 'Status', 'Raised']}>
      {rows.map((row) => (
        <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
          <td className="px-6 py-3">
            <RecordLink to={`/quotes/${row.id}`}>{row.request_number}</RecordLink>
          </td>
          <td className="px-6 py-3 text-gray-700 dark:text-gray-300">
            {row.insurance_type_name || '—'}
          </td>
          <td className="px-6 py-3">
            <Badge variant={getStatusBadgeVariant(row.status)} size="sm">
              {titleCase(row.status)}
            </Badge>
          </td>
          <td className="px-6 py-3 text-gray-500 dark:text-gray-400">
            {row.created_at ? formatDate(row.created_at) : '—'}
          </td>
        </tr>
      ))}
    </RecordTable>
  );
}

function ClaimsTable({ rows }: { rows: Record<string, any>[] }) {
  return (
    <RecordTable headers={['Claim', 'Policy', 'Type', 'Status', 'Incident']}>
      {rows.map((row) => (
        <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
          <td className="px-6 py-3">
            <RecordLink to={`/claims/${row.id}`}>{row.claim_number}</RecordLink>
          </td>
          <td className="px-6 py-3 text-gray-700 dark:text-gray-300">
            <PolicyNumber source={row} />
          </td>
          <td className="px-6 py-3 text-gray-700 dark:text-gray-300">
            {row.claim_type_display || titleCase(row.claim_type || '')}
          </td>
          <td className="px-6 py-3">
            <Badge variant={getStatusBadgeVariant(row.status)} size="sm">
              {titleCase(row.status)}
            </Badge>
          </td>
          <td className="px-6 py-3 text-gray-500 dark:text-gray-400">
            {row.incident_date ? formatDate(row.incident_date) : '—'}
          </td>
        </tr>
      ))}
    </RecordTable>
  );
}

/**
 * A renewal has no number of its own: it is identified by the policy it
 * renews. B9: `row.id` and `row.policy_id` are the same value on the queue,
 * but the link uses `policy_id` explicitly -- the detail route resolves
 * either a policy or a legacy Renewal id, and this is always the former.
 */
function RenewalsTable({ rows }: { rows: RenewalQueueRow[] }) {
  return (
    <RecordTable headers={['Policy', 'Insurer', 'Premium', 'Status', 'Expires']}>
      {rows.map((row) => (
        <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
          <td className="px-6 py-3">
            <PolicyNumber
              source={row}
              render={(text) => <RecordLink to={`/renewals/${row.policy_id}`}>{text}</RecordLink>}
            />
          </td>
          <td className="px-6 py-3 text-gray-700 dark:text-gray-300">{row.insurer_name || '—'}</td>
          <td className="px-6 py-3 text-gray-900 dark:text-gray-100">
            {row.current_premium != null ? formatCurrency(row.current_premium) : '—'}
          </td>
          <td className="px-6 py-3">
            {/* No workflow row yet means nobody has started working this
                renewal -- "Not started" says that plainly instead of showing
                the server's internal default status. */}
            <Badge variant={row.workflow_status ? getStatusBadgeVariant(row.workflow_status) : 'default'} size="sm">
              {renewalWorkflowStatusLabel(row.workflow_status)}
            </Badge>
          </td>
          <td className="px-6 py-3 text-gray-500 dark:text-gray-400">
            {row.expiry_date ? formatDate(row.expiry_date) : '—'}
          </td>
        </tr>
      ))}
    </RecordTable>
  );
}
